import { PermRole, UserRole } from '@/models/user-data.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AzureUser, DirectoryUser, JobTag, PhoneType, UserPhone, UserTag, ViewPerson } from '@/models/directory-user.model';
import { teamData } from '@/models/team-member.model';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';

export interface UpdateUser {
  user: Partial<DirectoryUser>;
  phones?: UserPhone[];
  aliases?: number[];
  locations?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class DirectoryService {
  private BASE_URL = environment.landmark_API_URL + '/directory';

  private messageService = inject(MessageService);

  userList = signal<DirectoryUser[]>([]);

  bulkUsers = signal<ViewPerson[]>([]);

  availableTags = signal<JobTag[]>([]);

  officePerms = signal<PermRole[]>([]);

  selectedUser = signal<DirectoryUser | null>(null);

  rolesList = signal<UserRole[]>([]);

  showEditForm = signal<boolean>(false);

  formLoading = signal<boolean>(false);

  phoneTypes:PhoneType[] = [
    {name: 'Work', id: 1, description: 'Work'},
    {name: 'Mobile', id: 2, description: 'Mobile'},
    {name: 'Home', id: 3, description: 'Home'}
  ]

  result = signal<DirectoryUser | AzureUser |null>(null);

  loadingSearch = signal<boolean>(false);

  constructor(private http: HttpClient) { 
    this.populateDirectory();
    this.populateOfficePerms();
    this.populateAvailableTags();
  }

  getFilteredUserList(officeList: number[]): Observable<DirectoryUser[]> {
    const params = new HttpParams().set('ids', officeList.join(','));
    return this.http.get<DirectoryUser[]>(`${this.BASE_URL}/filtered-list`, {params});
  }

  populateDirectory() {
    console.log('populating directory');
    this.http.get<DirectoryUser[]>(`${this.BASE_URL}/filtered`).subscribe(users => {
      this.userList.set(users.sort((a, b) => {
        if (!a.LastName && !b.LastName) return 0;
        if (!a.LastName) return 1;
        if (!b.LastName) return -1;
        return a.LastName.localeCompare(b.LastName);
      }));
      console.log('directory populated', this.userList());
    });
  }

  setSelectedUser(userid: number) {
    const user = this.userList().find(u => u.UID === userid);
    if (user) {
      this.selectedUser.set(user);
    }
  }

  populateOfficePerms() {
    console.log('populating office perms');
    this.http.get<PermRole[]>(`${this.BASE_URL}/office-perms`).subscribe(perms => {
      console.log('received locations:', perms);
      this.officePerms.set(perms.sort((a, b) => a.Franchises?.CityCode?.localeCompare(b.Franchises?.CityCode || '') || 0));
    });
  }

  populatePermsList(userid: number) {
    this.rolesList.set([]);
    console.log('populating proles list');
    this.http.get<UserRole[]>(`${this.BASE_URL}/roles-list/${userid}`).subscribe(roles => {
      console.log('received roles list:', roles);
      this.rolesList.set(roles);
    });
  }

  populateLocalPermsList(user: DirectoryUser) {
    const roles = user.UserRoles.filter(r => r.PermRoles.location !== true) || [];
    console.log('local perms list', roles);
    this.rolesList.set(roles);
  }

  archiveUser(userid: number) {
    console.log('archiving user', userid);
    this.http.patch(`${this.BASE_URL}/archive/${userid}`, {archived: true}).subscribe(res => {
      console.log('user archived', res);
      this.removeLocalUser(userid);
    });
  }

  populateAvailableTags() {
    console.log('populating available tags');
    this.http.get<JobTag[]>(`${this.BASE_URL}/available-tags`).subscribe(tags => {
      this.availableTags.set(tags);
    });
  }

  findDirectoryUser(email: string): DirectoryUser | null {
    const user: DirectoryUser | null = this.userList().find(u => u.User === email) || null;
    return user;
  }

  clearResult() {
    this.result.set(null);
    this.loadingSearch.set(false);
  }

  findUser(email: string) {
    this.loadingSearch.set(true);
    this.result.set(null);
    let user: DirectoryUser | AzureUser | null = null;
    user = this.findDirectoryUser(email);
    if (user) {
      console.log('found local user', user);
      this.result.set(user);
      this.loadingSearch.set(false);
    }
    else {
      console.log('no local user, searching azure');
      this.http.get<AzureUser>(`${this.BASE_URL}/azure/${email}`).subscribe(
        user => {
        console.log('received azure user', user);
        if(user) {
          this.result.set(user);
        }
        else {
          this.result.set(null);
        }
        this.loadingSearch.set(false);
      },
      error => {
        console.log('error finding azure user', error);
        this.result.set(null);
      }
    );
    }
  }

  private removeLocalUser(userid: number) {
    this.userList.update(users => users.filter(u => u.UID !== userid));
  }

  private updateLocalUser(user: DirectoryUser, uid: number) {
    const index = this.userList().findIndex(u => u.UID === uid);
    if (index !== -1) {
      this.userList.update(users => {
        users[index] = user;
        return users;
      });
    }
  }

  updateUser(uid: number, user: UpdateUser) {
    this.formLoading.set(true);
    this.http.patch<DirectoryUser>(`${this.BASE_URL}/${uid}`, user).subscribe(res => {
      console.log('user updated', res);
      this.updateLocalUser(res, uid);
      this.showEditForm.set(false);
      this.formLoading.set(false);
      this.messageService.add({severity:'success', summary:'Success', detail:'User updated'});
    },
    error => {
      console.log('error updating user', error);
      this.formLoading.set(false);
      this.messageService.add({severity:'error', summary:'Error', detail:'Failed to update user'});
      this.formLoading.set(false);
    }
  );
  }
  
  getInitials(user: DirectoryUser | teamData | null) {
    if(!user) return 'N/A';
    const firstName = user?.FirstName || '';
    const lastName = user?.LastName || '';
    return (firstName[0] || '') + (lastName[0] || '');
  }

  localBulkLocation(userUIDs: number[], permRoleID: number) {
    const permRole = this.officePerms().find(p => p.PermRoleID === permRoleID);
    if (!permRole) {
      console.warn(`PermRole with ID ${permRoleID} not found in officePerms`);
      return;
    }
    this.userList.update(users => {
      return users.map(user => {
        if (!userUIDs.includes(user.UID)) {
          return user;
        }
        const hasRole = user.UserRoles?.some(ur => ur.PermRoleID === permRoleID);
        
        if (hasRole) {
          return user;
        }
        const permRolesWithLocation: PermRole & { location?: boolean } = {
          ...permRole,
          roleAppNav: permRole.roleAppNav || [],
          RoleAccess: permRole.RoleAccess || []
        };
        (permRolesWithLocation as any).location = (permRole as any).location !== undefined 
          ? (permRole as any).location 
          : true;
        
        const newUserRole: UserRole = {
          UID: user.UID,
          PermRoleID: permRoleID,
          UserRoleID: 0,
          PermRoles: permRolesWithLocation as PermRole
        };
        return {
          ...user,
          UserRoles: [...(user.UserRoles || []), newUserRole]
        };
      });
    });
  }

  localBulkTags(userUIDs: number[], tagId: number) {
    const jobTag = this.availableTags().find(t => t.id === tagId);
    if (!jobTag) {
      console.warn(`JobTag with ID ${tagId} not found in availableTags`);
      return;
    }
    this.userList.update(users => {
      return users.map(user => {
        if (!userUIDs.includes(user.UID)) {
          return user;
        }
        const hasTag = user.user_tags_user_tags_uidToUsers?.some(ut => ut.tag_id === tagId);
        
        if (hasTag) {
          return user;
        }
        
        const newUserTag: UserTag = {
          uid: user.UID,
          tag_id: tagId,
          assigned_at: new Date(),
          assigned_by: 0,
          jobTags: jobTag
        };
        
        return {
          ...user,
          user_tags_user_tags_uidToUsers: [...(user.user_tags_user_tags_uidToUsers || []), newUserTag]
        };
      });
    });
  }

  bulkLocation(users: number[], location: number) {
    console.log('bulk updating locations', users, location);
    const body = {
      users: users
    }
    this.http.patch(`${this.BASE_URL}/bulk/location/${location}`, {users}).subscribe(res => {
      console.log('locations updated', res);
      this.localBulkLocation(users, location);
    });
  }

  bulkTags(users: number[], tag: number) {
    const body = {
      users: users
    }
    console.log('bulk updating tags', users, tag);
    this.http.patch(`${this.BASE_URL}/bulk/role/${tag}`, body).subscribe(res => {
      console.log('tags updated', res);
      this.localBulkTags(users, tag);
    });
  }
}